import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import Flatpickr from 'react-flatpickr';
import defaultConfig from './defaultConfig';
import '../../../../../scss/pages/pressrelease/_flatpickr.scss';
import styles from './Datepicker.module.scss';

/**
 * DatePicker is a lightweight wrapper around the Flatpickr date picker library.
 * It provides a consistent interface for date pickers across our React applications.
 *
 * Flatpickr documentation: https://flatpickr.js.org/
 *
 * Datepickr is a controlled component, but it's important to note that Flatpickr event handlers
 * have a custom signature, rather than the standard React SyntheticEvent object:
 * - selectedDates: An array of Date objects representing the currently selected dates.
 * - dateStr: A string representing the currently selected date(s) in the specified format.
 * - instance: The Flatpickr instance.
 * When using with useFormControls, you may need to define a higher-order function to wrap the
 * form event handlers (e.g., setField, validateField) like so:
 *
 * const handleDatepickerChange = (fieldName) => (selectedDates, dateStr, instance) => {
 *  validateField({
 *   target: {
 *    name: fieldName,
 *   value: dateStr,
 *  },
 * });
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.className - An optional CSS class name for the input container
 * @param {Object} props.config - The configuration options for the DatePicker
 * @param {string} props.labelText - The label text for the DatePicker input
 * @param {string} props.name - The name attribute for the DatePicker input
 * @param {function} props.onChange - The callback function called when the DatePicker value changes
 * @param {function} props.onClose - The callback function called when the DatePicker closes
 * @param {function} props.onOpen - The callback function called when the DatePicker opens
 * @param {function} props.onReady - The callback function called when the DatePicker is ready
 * @param {string} props.placeholder - The placeholder text for the DatePicker input
 * @param {string} props.value - The value of the DatePicker input
 * @returns {JSX.Element} The DatePicker component
 */
const DatePicker = forwardRef(({
  children = null,
  className = '',
  config = {},
  errorMsg = '',
  labelText = '',
  name = '',
  onChange,
  onClose = () => { },
  onOpen = () => { },
  onReady = () => { },
  placeholder = '',
  required = false,
  value = '',
  description = '',
}, ref) => {
  const mergedConfig = { ...defaultConfig, ...config };
  return (
    <div className={className}>
      {labelText && (
        <label htmlFor={name} className={styles.label}>
          <span id={`${name}-label`}>{labelText}</span>
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      {description && <div className={styles.description}>{description}</div>}
      <Flatpickr
        ref={ref}
        aria-labelledby={`${name}-label`}
        id={name}
        name={name}
        onChange={onChange}
        onClose={onClose}
        onOpen={onOpen}
        onReady={onReady}
        options={mergedConfig}
        placeholder={placeholder}
        value={value}
      >
        {children}
      </Flatpickr>
      {errorMsg && <p name="input-field-error" className={styles.error}>{errorMsg}</p>}
    </div>
  );
});

DatePicker.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  config: PropTypes.object,
  errorMsg: PropTypes.string,
  labelText: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  onReady: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  description: PropTypes.string,
};

export default DatePicker;
